import { GatsbyImage, getImage, IGatsbyImageData, ImageDataLike } from 'gatsby-plugin-image';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useImagePriority } from '../../../contexts/image-priority-context';
import { CsAssetSize } from '../../../sourcing/data/contentstack/content-item';
import { getSizesProp } from '../../../sourcing/data/contentstack/asset-helpers';

// Extended link props to include lowercase HTML attributes for preload hints
// These are valid HTML attributes but not in React's type definitions yet
type PreloadLinkProps = React.LinkHTMLAttributes<HTMLLinkElement> & {
    imagesrcset?: string;
    imagesizes?: string;
    fetchpriority?: 'high' | 'low' | 'auto';
};

/**
 * Re-export CsAssetSize for convenience when using Image component
 * Use getSizesProp(CsAssetSize.XXX) to get the sizes string:
 *
 * Viewport-based (for full-bleed layouts outside containers):
 * - FULL_100VW: '100vw' - Full viewport width
 * - HALF_100VW: '(max-width: 767px) 100vw, 50vw' - Half viewport (2 columns)
 * - THIRD_100VW: '(max-width: 767px) 100vw, 33.33vw' - Third viewport (3 columns)
 * - QTR_100VW: '(max-width: 767px) 100vw, 25vw' - Quarter viewport (4 columns)
 *
 * Container-constrained (for content inside 1440px max-width container):
 * - FULL_MAX_WIDTH: '(max-width: 1440px) 100vw, 1440px' - Full container width
 * - HALF_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 50vw, 720px' - Half container
 * - THIRD_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 33.33vw, 480px' - Third container
 * - QTR_MAX_WIDTH: '(max-width: 767px) 100vw, (max-width: 1440px) 25vw, 360px' - Quarter container
 */
export { CsAssetSize, getSizesProp };

interface ImageProps {
    image?: Partial<ContentStackImage> | null
    alt?: string
    style?: React.CSSProperties
    className?: string
    loading?: 'eager' | 'lazy'
    fetchPriority?: 'high' | 'low' | 'auto'
    sizes?: string
    [key: string]: unknown
}

const Image = ({
    image, loading: loadingProp, alt, fetchPriority: fetchPriorityProp, sizes: sizesProp, ...rest
}: ImageProps): JSX.Element => {
    // Get loading strategy from context, but allow prop overrides
    const contextPriority = useImagePriority();
    const loading = loadingProp ?? contextPriority.loading;
    const fetchPriority = fetchPriorityProp ?? contextPriority.fetchPriority;
    const {
        filename,
        contentStackAsset,
        description,
        url,
    } = image || {};

    const altText = alt || description || '';
    const imagePath = filename || url;

    if (imagePath && url && (imagePath.includes('.svg') || imagePath.includes('.gif'))) {
        // Transform URL to use edge function if in Netlify environment
        // Only transform if it's a full ContentStack URL
        let transformedUrl = url;
        if (process.env.NETLIFY && url.startsWith('https://images.contentstack.io/')) {
            try {
                transformedUrl = `/dcs-images${new URL(url).pathname}`;
            } catch {
                transformedUrl = url;
            }
        }

        const imgProps = { ...rest, ...(fetchPriority && { fetchpriority: fetchPriority }) };
        const linkProps = {
            rel: 'preload' as const,
            as: 'image' as const,
            href: transformedUrl,
            fetchpriority: fetchPriority,
        };
        return (
            <>
                {loading === 'eager' && fetchPriority === 'high' && (
                    <Helmet>
                        <link {...linkProps as PreloadLinkProps} />
                    </Helmet>
                )}
                <img
                    loading={loading}
                    src={transformedUrl}
                    alt={altText}
                    {...imgProps as React.ImgHTMLAttributes<HTMLImageElement>}
                />
            </>
        );
    }

    if (contentStackAsset && typeof contentStackAsset === 'object' && 'childImageSharp' in contentStackAsset && getImage(contentStackAsset?.childImageSharp as ImageDataLike)) {
        const img = getImage(contentStackAsset?.childImageSharp as ImageDataLike) as IGatsbyImageData;

        // Use provided sizes or intelligent default based on priority
        const sizes = sizesProp || (loading === 'eager' && fetchPriority === 'high'
            ? getSizesProp(CsAssetSize.FULL_100VW)
            : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));

        const linkProps = img.images.fallback ? {
            rel: 'preload' as const,
            as: 'image' as const,
            href: img.images.fallback.src,
            imagesrcset: img.images.fallback.srcSet,
            imagesizes: sizes,
            fetchpriority: fetchPriority,
        } : null;
        return (
            <>
                {loading === 'eager' && fetchPriority === 'high' && linkProps && (
                    <Helmet>
                        <link {...linkProps as PreloadLinkProps} />
                    </Helmet>
                )}
                <GatsbyImage
                    image={img}
                    alt={altText}
                    loading={loading}
                    {...rest}
                    {...(fetchPriority && { fetchpriority: fetchPriority })}
                    {...(sizes && { sizes })}
                />
            </>
        );
    } if (image && getImage(image)) {
        const img = getImage(image as ImageDataLike) as IGatsbyImageData;

        // Use provided sizes or intelligent default based on priority
        const sizes = sizesProp || (loading === 'eager' && fetchPriority === 'high'
            ? getSizesProp(CsAssetSize.FULL_100VW)
            : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));

        const linkProps = img.images.fallback ? {
            rel: 'preload' as const,
            as: 'image' as const,
            href: img.images.fallback.src,
            imagesrcset: img.images.fallback.srcSet,
            imagesizes: sizes,
            fetchpriority: fetchPriority,
        } : null;
        return (
            <>
                {loading === 'eager' && fetchPriority === 'high' && linkProps && (
                    <Helmet>
                        <link {...linkProps as PreloadLinkProps} />
                    </Helmet>
                )}
                <GatsbyImage
                    image={img}
                    alt={altText}
                    loading={loading}
                    {...rest}
                    {...(fetchPriority && { fetchpriority: fetchPriority })}
                    {...(sizes && { sizes })}
                />
            </>
        );
    }

    if (imagePath && url) {
        // Fallback for images without contentStackAsset data
        // Add basic responsive sizing using ContentStack's URL API
        const isContentStackImage = url.startsWith('https://images.contentstack.io/');

        // Generate responsive srcset for ContentStack images
        const generateResponsiveSrcset = (baseUrl: string): string => {
            if (!isContentStackImage) return '';

            // Standard responsive breakpoints (same as used in asset-helpers.ts)
            const breakpoints = [360, 540, 720, 1080, 1440, 1920];
            const urlObj = new URL(baseUrl);

            return breakpoints.map((width) => {
                const params = new URLSearchParams(urlObj.search);
                params.set('width', width.toString());
                params.set('quality', '50');

                let srcUrl = `${urlObj.origin}${urlObj.pathname}?${params.toString()}`;
                if (process.env.NETLIFY) {
                    srcUrl = `/dcs-images${new URL(srcUrl).pathname}${new URL(srcUrl).search}`;
                }
                return `${srcUrl} ${width}w`;
            }).join(', ');
        };

        // Transform URL to use edge function if in Netlify environment
        let transformedUrl = url;
        if (process.env.NETLIFY && isContentStackImage) {
            try {
                transformedUrl = `/dcs-images${new URL(url).pathname}`;
            } catch {
                transformedUrl = url;
            }
        }

        const imgProps = { ...rest, ...(fetchPriority && { fetchpriority: fetchPriority }) };
        const srcset = isContentStackImage ? generateResponsiveSrcset(url) : undefined;

        // Smart sizes default: Use provided sizes or intelligent default based on priority
        // Hero/critical images (eager + high priority) are typically full-width
        // Content images are typically constrained by the 1440px container
        const sizes = sizesProp || (loading === 'eager' && fetchPriority === 'high'
            ? getSizesProp(CsAssetSize.FULL_100VW)
            : getSizesProp(CsAssetSize.FULL_MAX_WIDTH));

        const linkProps = {
            rel: 'preload' as const,
            as: 'image' as const,
            href: transformedUrl,
            ...(srcset && { imagesrcset: srcset, imagesizes: sizes }),
            fetchpriority: fetchPriority,
        };

        return (
            <>
                {loading === 'eager' && fetchPriority === 'high' && (
                    <Helmet>
                        <link {...linkProps as PreloadLinkProps} />
                    </Helmet>
                )}
                <img
                    loading={loading}
                    src={transformedUrl}
                    {...(srcset && { srcSet: srcset })}
                    {...(srcset && { sizes })}
                    alt={altText}
                    {...imgProps as React.ImgHTMLAttributes<HTMLImageElement>}
                />
            </>
        );
    }

    // Should be able to function as a 'box' layout wise even with a missing image
    return <div {...rest} />;
};

export default Image;
